import { createSlice } from '@reduxjs/toolkit';
import { getDefaultView } from '@/pages/reports/non-ats-reports/shared/utils/date-group';
import { formatLocalDate } from '@/utils/dateUtils';

const initialState = {
  filterSynced: false,
  filters: {
    //view only
    display: 'all', // table or chart or all
    chart: 'bar',
    metrics: 'overall',

    // API date
    date_group: 'W',

    from: 'past12m',
    to: null,

    c_from: null,
    c_to: null,

    group_by: null,
    break_down: null,

    user_activity_group_by: null,
    user_activity_break_down: null,

    flow_id: [],
    flow_id_condition: null,

    recruiter_id: [],
    recruiter_id_condition: null,

    department_id: [],
    department_id_condition: null,

    job_id: [],
    job_id_condition: null,

    filter_by: [],
    top_by: 'count',
  },
};

const { reducer, actions } = createSlice({
  name: 'reports-flow-health',
  initialState,

  reducers: {
    setFilter: (state, { payload }) => {
      const { type, value } = payload;
      if (type === 'from') {
        const dateGroupValue = getDefaultView(value);
        state.filters = {
          ...state.filters,
          from: value,
          to: null,
          date_group: dateGroupValue,
        };
        return;
      }

      state.filters = {
        ...state.filters,
        [type]: value,
      };
    },

    setTimeFrame: (state, { payload }) => {
      const { value } = payload;
      const { from, to } = value;
      const dateGroupValue = getDefaultView(from, to);

      state.filters = {
        ...state.filters,
        from: formatLocalDate(from),
        to: formatLocalDate(to),
        date_group: dateGroupValue,
      };
    },

    setCompare: (state, { payload }) => {
      const { value } = payload;
      const { condition, rdate, date } = value;
      const initial = { c_from: null, c_to: null };
      if (date) {
        const compare = {
          ...initial,
          [`c_${condition?.id}`]: formatLocalDate(date),
        };
        state.filters = {
          ...state.filters,
          ...compare,
        };
        return;
      }
      const compare = {
        ...initial,
        [`c_${condition?.id}`]: rdate,
      };
      state.filters = {
        ...state.filters,
        ...compare,
      };
    },

    resetGroups: (state) => {
      state.filters = {
        ...state.filters,
        group_by: null,
        break_down: null,
        user_activity_group_by: null,
        user_activity_break_down: null,
      };
    },

    resetCompare: (state) => {
      state.filters = {
        ...state.filters,
        c_from: null,
        c_to: null,
      };
    },

    clearFilter: (state, { payload }) => {
      const { type } = payload;
      const initialValue = initialState?.filters?.[type];
      state.filters = { ...state.filters, [type]: initialValue };
    },

    setFilterBy: (state, { payload }) => {
      const { value } = payload;
      const payloadToApply = value?.filter(
        (f) => f?.filter_term?.id && f?.filter_by?.length > 0
      );
      const filterToApply = payloadToApply?.reduce((acc, curr) => {
        acc[curr?.filter_term?.id] = curr?.filter_by;
        acc[`${curr?.filter_term?.id}_condition`] = curr?.filter_condition;
        return acc;
      }, {});

      state.filters = {
        ...state.filters,
        flow_id: [],
        flow_id_condition: null,
        recruiter_id: [],
        recruiter_id_condition: null,
        department_id: [],
        department_id_condition: null,
        job_id: [],
        job_id_condition: null,
        filter_by: payloadToApply,
        ...filterToApply,
      };
    },

    removeFilterBy: (state, { payload }) => {
      const { id } = payload;
      const filters = {
        [id]: null,
        [`${id}_condition`]: null,
      };
      const filter_by = state?.filters?.filter_by?.filter(
        (f) => f.filter_term?.id !== id
      );
      state.filters = { ...state.filters, ...filters, filter_by };
    },

    resetFilterBy: (state) => {
      state.filters = {
        ...state.filters,
        flow_id: [],
        flow_id_condition: null,

        recruiter_id: [],
        recruiter_id_condition: null,

        department_id: [],
        department_id_condition: null,

        job_id: [],
        job_id_condition: null,

        filter_by: null,
      };
    },
  },

  extraReducers: {},
});

export const {
  setFilter,
  setTimeFrame,
  setCompare,
  clearFilter,
  setFilterBy,
  resetFilterBy,
  resetCompare,
  removeFilterBy,
  resetGroups,
} = actions;
export default reducer;
